/**
 * Localized Date Component
 *
 * @author    Eric Pratt <erpratt@wayfair.com>
 * @author    Frontend Design Systems <FrontendDesignSystems@wayfair.com>
 * @copyright 2019 Wayfair LLC - All rights reserved
 */
import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {localeContext, LOCALES} from '@homebase/provider';
import {format as formatDate, parse} from 'date-fns';
import {DATE_FORMATS, getDateFormatForLocale} from './localized-date-constants';

const LocalizedDate = ({format, value, locale: propsLocale}) => {
  const contextLocale = useContext(localeContext);
  const parsedDate = parse(value);
  // If locale is specified from props, we want to accept that value
  // otherwise we want to use locale from context
  const locale =
    typeof propsLocale !== 'undefined' ? propsLocale : contextLocale;
  // Returning JSX here so the Homebase Site can properly document this component
  // There is a limitation in react-docgen (the package we use from the metadata loader to document props)
  // Where it relies on the function returning JSX
  return <>{formatDate(parsedDate, getDateFormatForLocale(format, locale))}</>;
};

LocalizedDate.propTypes = {
  // Accepts one of the formats from the `DATE_FORMATS` static on the component.
  // These formats look like: 9/5/19 | 9/5/2019 | 09/05/2019 | Sep 5, 2019 | September 5, 2019
  format: PropTypes.oneOf([
    'M_D_YY',
    'M_D_YYYY',
    'MM_DD_YYYY',
    'MMM_D_YYYY',
    'MMMM_D_YYYY',
  ]),
  // Accepts one of the formats from the `LOCALES` static on the component
  locale: PropTypes.oneOf(['de', 'en', 'en-CA', 'en-GB', 'es', 'fr', 'fr-CA']),
  // Date value to parse and display
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]).isRequired,
};

LocalizedDate.defaultProps = {
  format: 'MMMM_D_YYYY',
};

LocalizedDate.DATE_FORMATS = DATE_FORMATS;
LocalizedDate.LOCALES = LOCALES;

export {DATE_FORMATS, LOCALES};
export default LocalizedDate;
